import React from 'react'
import {Chart, ZoomButtons} from 'react-stockcharts'
import {CandlestickSeries} from 'react-stockcharts/lib/series'
import {XAxis, YAxis} from 'react-stockcharts/lib/axes'
import {MouseCoordinateX} from 'react-stockcharts/lib/coordinates'
import {
  Label,
  Annotate,
  buyPath,
  sellPath,
} from 'react-stockcharts/lib/annotation'
import {SingleValueTooltip} from 'react-stockcharts/lib/tooltip'
import SvgPathAnnotation from '../SvgPathAnnotation'
import {timeFormat} from 'd3-time-format'
import {format} from 'd3-format'

import style from './price.sass'

const getTooltipText = frequency => d => {
  let text = []

  const transaction = d.transaction
  if (transaction) {
    text = text.concat([{
      label: 'Number of shares',
      value: transaction.amount
    }, {
      label: 'Transaction Price',
      value: transaction.price > 0.1 ? transaction.price.toLocaleString('en-US',{style:'currency',currency:'USD'}) : format('$.4r')(transaction.price)
    }])

    if (transaction.ts) {
      const date = new Date(new Date(transaction.ts).getTime() + new Date(transaction.ts).getTimezoneOffset()*60000 - 4*60*60000)
      text.push({
        label: 'Transaction date',
        value: timeFormat('%Y-%m-%d')(date)
      })
      if (frequency.length > 8) {
        text.push({
          label: 'Transaction time',
          value: timeFormat('%H:%M:%S')(date)
        })
      }
    }
  }

  const data = d.triggeredDays
  for (let name in data) {
    let value
    if (data[name]) {
      if (typeof data[name].value === 'number') {
        value = data[name].value > 0.1 ? format('.2f')(data[name].value) : format('.4r')(data[name].value)
      } else {
        value = '-'
      }
      if (data[name].unit) {
        if (data[name].unit.location === 'before') {
          value = data[name].unit.symbol+value
        }
        if (data[name].unit.location === 'after') {
          value = value+data[name].unit.symbol
        }
      }
    } else {
      value = '-'
    }
    text.push({
      label: name.charAt(0).toUpperCase() + name.slice(1),
      value: value
    })
  }
  if (d.message) {
    text.push({
      label: d.message.charAt(0).toUpperCase() + d.message.slice(1)
    })
  }
  return text
}

const getFill = d => {
  if (d.triggeredOnAggr === false) {
    return 'rgb(76, 160, 80)'
  } else if (d.transaction?.ts) {
    return 'rgb(76, 160, 80)'
  } else {
    return 'rgb(100, 100, 100)'
  }
}

const getStroke = d => {
  if (d.triggeredOnAggr !== false || d.transaction && !d.transaction.ts) {
    return 'none'
  } else if (d.transaction) {
    return 'rgb(76, 160, 80)'
  } else {
    return 'none'
  }
}

export const getPriceChart = (width, margin, yExtents, ticker, reset, frequency, mouseCoordinateWidth) => {
  const tooltipText = getTooltipText(frequency)
  const openAnnotationProps = {
    y: ({ yScale, datum }) => {
      if (datum.transaction && datum.transaction.price) {
        return yScale(datum.transaction.price)
      } else {
        return 0
      }
    },
    fill: getFill,
    stroke: getStroke,
    path: buyPath,
    tooltip: d => tooltipText(d),
    tooltipClassName: style.tooltip
  }

  const coverAnnotationProps = {
    y: ({ yScale, datum }) => {
      if (datum.transaction && datum.transaction.price) {
        return yScale(datum.transaction.price)
      } else {
        return 0
      }
    },
    fill: d => d.triggeredOnAggr === false ? 'rgb(195, 67, 54)' : d.transaction ? 'rgb(195, 67, 54)' : 'rgb(100, 100, 100)',
    stroke: d => d.triggeredOnAggr === false ? d.transaction ? 'none' : 'rgb(100, 100, 100)' : 'none',
    path: sellPath,
    tooltip: d => tooltipText(d),
    tooltipClassName: style.tooltip
  }

  return(
    <Chart
      id='price'
      height={350}
      origin={[0,0]}
      yExtents={yExtents}
    >
      <ZoomButtons onReset={reset}/>
      <rect
        width={width - margin.left - margin.right}
        x={1}
        height={350}
        stroke='#000000'
        strokeOpacity={0.3}
        strokeWidth={1}
        fill='none'
      />
      <XAxis
        axisAt='bottom'
        orient='bottom'
        opacity={0}
        tickStrokeOpacity={0.1}
        innerTickSize={-350}
      />
      <YAxis
        axisAt='left'
        orient='left'
        ticks={5}
        tickFormat={d => Math.abs(d) > 1 ? format('$')(d) : format('$.2r')(d)}
        tickStrokeOpacity={0.1}
        innerTickSize={-(width - margin.left - margin.right)}
      />
      {!!ticker &&
        <Label
          x={(width - margin.left - margin.right)/2}
          y={20}
          fontSize={16}
          text={ticker}
        />
      }
      <SingleValueTooltip
        yLabel='O.'
        valueFill='black'
        fontSize={12}
        yAccessor={d => d.open}
        origin={[20, 20]}
        yDisplayFormat={d => d > 0.1 ? format('$.2f')(d) : format('$.4r')(d)}
      />
      <SingleValueTooltip
        yLabel='C.'
        valueFill='black'
        fontSize={12}
        yAccessor={d => d.close}
        origin={[90, 20]}
        yDisplayFormat={d => d > 0.1 ? format('$.2f')(d) : format('$.4r')(d)}
      />
      <SingleValueTooltip
        yLabel='H.'
        valueFill='black'
        fontSize={12}
        yAccessor={d => d.high}
        origin={[20, 40]}
        yDisplayFormat={d => d > 0.1 ? format('$.2f')(d) : format('$.4r')(d)}
      />
      <SingleValueTooltip
        yLabel='L.'
        valueFill='black'
        fontSize={12}
        yAccessor={d => d.low}
        origin={[90, 40]}
        yDisplayFormat={d => d > 0.1 ? format('$.2f')(d) : format('$.4r')(d)}
      />
      <MouseCoordinateX
        at='bottom'
        orient='bottom'
        displayFormat={timeFormat(frequency)}
        rectWidth={mouseCoordinateWidth}
      />
      <CandlestickSeries
        stroke='rgb(31, 119, 180)'
        strokeWidth={2}
      />
      <Annotate with={SvgPathAnnotation} when={d => !!d.triggeredDays && d.type === 'open'}
        usingProps={openAnnotationProps} />
      <Annotate with={SvgPathAnnotation} when={d => !!d.triggeredDays && d.type === 'cover'}
        usingProps={coverAnnotationProps} />
    </Chart>
  )
}
